import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Eye, Smartphone, Monitor, CheckCircle, AlertTriangle, Camera, ExternalLink, ChevronLeft, ChevronRight, Share, QrCode } from 'lucide-react';
import { useCategory, useRecordActivity, useActivityTimer, useHierarchicalProgress, useUserActivities} from '../../hooks';
import { Card, LoadingSpinner, Button, Badge, Alert } from '../../components/common';

import QuickUserActions from '@/components/users/QuickUserActions';

const ARPage: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const { data: categoryData, isLoading } = useCategory(categoryId!);
  const recordActivity = useRecordActivity();
  const { getElapsedSeconds } = useActivityTimer();
  
  const [arStarted, setArStarted] = useState(false);
  const [arCompleted, setArCompleted] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [selectedArIndex, setSelectedArIndex] = useState(0);
  const [viewedArExperiences, setViewedArExperiences] = useState<Set<string>>(new Set());
  const [showQRCode, setShowQRCode] = useState(false);

  const category = categoryData?.category;
  const arContents = category?.arContents || [];
  const selectedArContent = arContents[selectedArIndex];

  const { data: activitiesData } = useUserActivities({ categoryId: categoryId! });

  const progress = useHierarchicalProgress(
    categoryData?.category?.subcategories || [],
    activitiesData?.users || []
  );


   
  // Detectar si es móvil
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  const startARExperience = async () => {
    setArStarted(true);
    
    await recordActivity.mutateAsync({
      categoryId: categoryId!,
      activityType: 'ar',
      completed: false,
      timeSpent: getElapsedSeconds(),
      metadata: {
        arContentId: selectedArContent?.id,
        arContentTitle: selectedArContent?.title,
        deviceType: isMobile ? 'mobile' : 'desktop',
        action: 'started'
      }
    });
  };

  const completeARExperience = async () => {
    const newViewedExperiences = new Set(viewedArExperiences);
    newViewedExperiences.add(selectedArContent?.id || '');
    setViewedArExperiences(newViewedExperiences);

    const allCompleted = newViewedExperiences.size === arContents.length;
    setArCompleted(allCompleted);
    setArStarted(false);

    await recordActivity.mutateAsync({
      categoryId: categoryId!,
      activityType: 'ar',
      completed: allCompleted,
      timeSpent: getElapsedSeconds(),
      metadata: {
        arContentId: selectedArContent?.id,
        arContentTitle: selectedArContent?.title,
        viewedArExperiences: Array.from(newViewedExperiences),
        totalArExperiences: arContents.length,
        deviceType: isMobile ? 'mobile' : 'desktop',
        allCompleted
      }
    });
  };

  const handleIframeLoad = () => {
    setIframeLoaded(true);
  };

  const openInNewTab = () => {
    if (selectedArContent?.arUrl) {
      // Para móvil, abrir en pantalla completa
      if (isMobile) {
        window.open(selectedArContent.arUrl, '_blank', 'fullscreen=yes,scrollbars=no,resizable=no');
      } else {
        window.open(selectedArContent.arUrl, '_blank', 'noopener,noreferrer');
      }
    }
  };

  const markAsCompleted = async () => {
    await completeARExperience();
  };

  const shareARExperience = () => {
    if (navigator.share && selectedArContent?.arUrl) {
      navigator.share({
        title: `AR: ${selectedArContent.title}`,
        text: `Mira esta experiencia de realidad aumentada: ${selectedArContent.title}`,
        url: selectedArContent.arUrl
      });
    } else {
      // Fallback: copiar al clipboard
      navigator.clipboard.writeText(selectedArContent?.arUrl || '');
      alert('Enlace copiado al portapapeles');
    }
  };

  const goToPreviousAR = () => {
    if (selectedArIndex > 0) {
      setSelectedArIndex(selectedArIndex - 1);
      setIframeLoaded(false);
    }
  };

  const goToNextAR = () => {
    if (selectedArIndex < arContents.length - 1) {
      setSelectedArIndex(selectedArIndex + 1);
      setIframeLoaded(false);
    }
  };

  const selectArExperience = (index: number) => {
    setSelectedArIndex(index);
    setIframeLoaded(false);
    if (arStarted) {
      setArStarted(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!category) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-brinsa-azul mb-4">
          Categoría no encontrada
        </h2>
        <Link to="/home">
          <Button variant="outline">
            Volver a categorías
          </Button>
        </Link>
      </div>
    );
  }

  if (arContents.length === 0) {
    return (
      <div className="text-center py-12">
        <Camera className="w-16 h-16 text-brinsa-azulOscuro/50 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-brinsa-azul mb-4">
          Contenido AR no disponible
        </h2>
        <p className="text-brinsa-azulOscuro/80 mb-6">
          Esta categoría no tiene contenido de realidad aumentada configurado aún.
        </p>
        <Link to={`/category/${categoryId}`}>
          <Button variant="outline">
            Volver a la categoría
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Navegación */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Link
          to={`/category/${categoryId}`}
          className="inline-flex items-center text-brinsa-azulOscuro/80 hover:text-brinsa-azul transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver a {category.name}
        </Link>
      </motion.div>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center"
      >
        <h1 className="text-3xl font-bold text-brinsa-azul mb-4">
          Realidad Aumentada: {category.name}
        </h1>
        <p className="text-lg text-brinsa-azulOscuro/80">
          {selectedArContent?.title} ({selectedArIndex + 1} de {arContents.length})
        </p>
      </motion.div>

      {/* Alerta para dispositivos móviles */}
      {isMobile && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <Alert
            type="info"
            title="Experiencia optimizada para móvil"
            message="En dispositivos móviles, la experiencia AR se abre en pantalla completa para mejor acceso a la cámara y sensores. Regresa aquí cuando termines para marcar la experiencia como completada."
          />
        </motion.div>
      )}

      {/* Selector de experiencias AR */}
      {arContents.length > 1 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <Card title="Experiencias AR disponibles">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {arContents.map((arContent, index) => (
                <button
                  key={arContent.id}
                  onClick={() => selectArExperience(index)}
                  className={`p-3 rounded-lg border-2 transition-all text-left ${
                    index === selectedArIndex
                      ? 'border-brinsa-morado bg-brinsa-morado/10'
                      : 'border-slate-200 hover:border-brinsa-celeste/40'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-brinsa-azul truncate">
                      {arContent.title}
                    </h4>
                    <div className="flex space-x-1">
                      {index === selectedArIndex && (
                        <Badge variant="info" size="sm">Activo</Badge>
                      )}
                      {viewedArExperiences.has(arContent.id) && (
                        <Badge variant="success" size="sm">
                          <CheckCircle className="w-3 h-3" />
                        </Badge>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-brinsa-azulOscuro/80 truncate">
                    {arContent.description}
                  </p>
                </button>
              ))}
            </div>
          </Card>
        </motion.div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Panel principal de AR */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="lg:col-span-2"
        >
          <Card>
            {!arStarted || isMobile ? (
              // Pantalla de inicio (siempre para móviles)
              <div className="p-8 text-center">
                <div className="w-20 h-20 bg-brinsa-morado/15 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Eye className="w-10 h-10 text-brinsa-morado" />
                </div>

                <h2 className="text-2xl font-bold text-brinsa-azul mb-4">
                  {selectedArContent?.title}
                </h2>

                <p className="text-brinsa-azulOscuro/80 mb-6">
                  {selectedArContent?.description}
                </p>

                <div className="bg-brinsa-celeste/10 p-6 rounded-lg mb-6 border border-brinsa-celeste/20">
                  <h3 className="font-semibold text-brinsa-azul mb-3">Instrucciones:</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="text-center">
                      <Smartphone className="w-8 h-8 text-brinsa-azul mx-auto mb-2" />
                      <div className="font-medium text-brinsa-azul">Móvil</div>
                      <div className="text-brinsa-azulOscuro/80">
                        {isMobile ? 'Se abre en pantalla completa' : 'Experiencia AR completa'}
                      </div>
                    </div>
                    <div className="text-center">
                      <Monitor className="w-8 h-8 text-brinsa-azul mx-auto mb-2" />
                      <div className="font-medium text-brinsa-azul">Escritorio</div>
                      <div className="text-brinsa-azulOscuro/80">Vista 3D interactiva</div>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  {/* Botones condicionales según dispositivo */}
                  {isMobile ? (
                    // Solo en móvil: botón de abrir en nueva pestaña
                    <>
                      <Button
                        onClick={openInNewTab}
                        className="w-full"
                        leftIcon={<ExternalLink className="w-4 h-4" />}
                      >
                        Abrir Experiencia AR
                      </Button>
                      
                      <Button
                        variant="outline"
                        onClick={shareARExperience}
                        className="w-full"
                        leftIcon={<Share className="w-4 h-4" />}
                      >
                        Compartir experiencia
                      </Button>

                      {/* Botón para marcar como completada después de usar AR */}
                      <div className="mt-6 p-4 bg-brinsa-verde/10 rounded-lg border border-brinsa-verde/20">
                        <p className="text-sm text-brinsa-azulOscuro/80 mb-3">
                          ¿Ya exploraste la experiencia AR? Márcala como completada:
                        </p>
                        <Button
                          variant="outline"
                          onClick={markAsCompleted}
                          className="w-full"
                          leftIcon={<CheckCircle className="w-4 h-4" />}
                        >
                          {viewedArExperiences.has(selectedArContent?.id || '') ? 
                            'Ya completada' : 
                            'Marcar como completada'
                          }
                        </Button>
                      </div>
                    </>
                  ) : (
                    // En desktop: ambos botones (iframe + nueva pestaña)
                    <>
                      <Button 
                        onClick={startARExperience}
                        className="w-full"
                        leftIcon={<Eye className="w-4 h-4" />}
                      >
                        Ver aquí (embebido)
                      </Button>
                      
                      <Button
                        variant="outline"
                        onClick={openInNewTab}
                        className="w-full"
                        leftIcon={<ExternalLink className="w-4 h-4" />}
                      >
                        Abrir en nueva pestaña
                      </Button>
                    </>
                  )}
                </div>

                {/* Navegación entre experiencias */}
                {arContents.length > 1 && (
                  <div className="flex items-center justify-center space-x-4 mt-6">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={goToPreviousAR}
                      disabled={selectedArIndex === 0}
                      leftIcon={<ChevronLeft className="w-4 h-4" />}
                    >
                      Anterior
                    </Button>
                    
                    <span className="text-sm text-brinsa-azulOscuro/80">
                      {selectedArIndex + 1} de {arContents.length}
                    </span>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={goToNextAR}
                      disabled={selectedArIndex === arContents.length - 1}
                      rightIcon={<ChevronRight className="w-4 h-4" />}
                    >
                      Siguiente
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              // Experiencia AR embebida (solo desktop)
              <div className="relative">
                <div className="aspect-video bg-slate-100 rounded-lg overflow-hidden relative">
                  {!iframeLoaded && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <LoadingSpinner size="lg" />
                        <p className="mt-4 text-brinsa-azulOscuro/80">Cargando experiencia AR...</p>
                      </div>
                    </div>
                  )}
                  
                  <iframe
                    key={selectedArContent?.id}
                    src={selectedArContent?.arUrl}
                    className="w-full h-full border-0"
                    allowFullScreen
                    allow="camera; microphone; gyroscope; accelerometer; xr-spatial-tracking"
                    onLoad={handleIframeLoad}
                    style={{ minHeight: '400px' }}
                  />
                </div>

                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-brinsa-azul">
                      {selectedArContent?.title}
                    </h3>
                    <div className="flex items-center space-x-2">
                      <Badge variant="info" size="sm">En vivo</Badge>
                      <Badge variant="default" size="sm">
                        {selectedArIndex + 1}/{arContents.length}
                      </Badge>
                    </div>
                  </div>

                  <p className="text-brinsa-azulOscuro/80 mb-4">
                    {selectedArContent?.description}
                  </p>

                  <div className="bg-brinsa-azul/15 p-4 rounded-lg mb-4 border border-brinsa-azul/30">
                    <h4 className="font-medium text-brinsa-azul mb-2">Consejos:</h4>
                    <ul className="text-sm text-brinsa-azul/90 space-y-1">
                      <li>• Usa el mouse para rotar la vista</li>
                      <li>• Algunos elementos son interactivos</li>
                      <li>• Para mejor experiencia, abre en pantalla completa</li>
                    </ul>
                  </div>

                  <div className="flex space-x-3">
                    <Button
                      variant="outline"
                      onClick={() => setArStarted(false)}
                      className="flex-1"
                    >
                      Volver al inicio
                    </Button>
                    
                    <Button
                      onClick={completeARExperience}
                      className="flex-1"
                      leftIcon={<CheckCircle className="w-4 h-4" />}
                    >
                      {viewedArExperiences.has(selectedArContent?.id || '') ? 
                        'Ya completada' : 
                        'Completar experiencia'
                      }
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </Card>
        </motion.div>

        {/* Panel lateral */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="space-y-6"
        >
          {/* Progreso de experiencias */}
          <Card title="Progreso de experiencias">
            <div className="space-y-3">
              <div className="flex justify-between text-sm text-brinsa-azulOscuro/80">
                <span>Experiencias completadas</span>
                <span>{viewedArExperiences.size}/{arContents.length}</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2">
                <div
                  className="bg-brinsa-morado h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(viewedArExperiences.size / arContents.length) * 100}%` }}
                />
              </div>
              <p className="text-xs text-brinsa-azulOscuro/80">
                {viewedArExperiences.size === arContents.length 
                  ? '¡Has completado todas las experiencias AR!'
                  : `Te faltan ${arContents.length - viewedArExperiences.size} experiencias por explorar`
                }
              </p>
            </div>
          </Card>

          {/* Información técnica */}
          <Card title="Información técnica">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-brinsa-azulOscuro/80">Tecnología:</span>
                <Badge variant="info" size="sm">8th Wall</Badge>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-brinsa-azulOscuro/80">Total experiencias:</span>
                <Badge variant="default" size="sm">{arContents.length}</Badge>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-brinsa-azulOscuro/80">Tipo de dispositivo:</span>
                <Badge variant="default" size="sm">
                  {isMobile ? 'Móvil' : 'Escritorio'}
                </Badge>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-brinsa-azulOscuro/80">Modo:</span>
                <Badge variant="default" size="sm">
                  {isMobile ? 'Pantalla completa' : 'Embebido + Externa'}
                </Badge>
              </div>
            </div>
          </Card>

          {/* Ayuda para móviles */}
          {isMobile && (
            <Card title="Instrucciones móvil">
              <div className="space-y-3 text-sm text-brinsa-azulOscuro/80">
                <p>1. Toca "Abrir Experiencia AR"</p>
                <p>2. Permite acceso a la cámara</p>
                <p>3. Explora la experiencia AR</p>
                <p>4. Regresa aquí y marca como completada</p>
              </div>
            </Card>
          )}
        </motion.div>
        
      </div>
      <QuickUserActions progress={progress}/>
    </div>
  );
};

export default ARPage;